<template>
  <div class="profile">
    <div class="profile__top">
      <div class="container">
        <div class="profile__top-wrapper">
          <el-breadcrumb separator=">">
            <el-breadcrumb-item :to="translations.i18nRoute({ name: 'home' })">{{ t('home.title') }}</el-breadcrumb-item>
            <el-breadcrumb-item>{{ getBreadcrumb() }}</el-breadcrumb-item>
          </el-breadcrumb>
          <h1 class="profile__title">{{ t('profile.title') }}</h1>
        </div>
      </div>
    </div>
    <div class="profile__main">
      <div class="container">
        <div class="profile__main-wrapper">
          <div class="profile__left">
            <nav class="profile__nav">
              <router-link
                  :to="translations.i18nRoute({ name: 'profile-info' })"
                  class="profile__nav-item"
                  :class="[{ active: routeName === 'profile-info' }]"
              >
                {{ t('profile.breadcrumbs.info') }}
              </router-link>
              <router-link
                  :to="translations.i18nRoute({ name: 'profile-cart' })"
                  class="profile__nav-item"
                  :class="[{ active: routeName === 'profile-cart' }]"
              >
                {{ t('profile.breadcrumbs.cart') }}
              </router-link>
              <router-link
                  :to="translations.i18nRoute({ name: 'profile-wishlist' })"
                  class="profile__nav-item"
                  :class="[{ active: routeName === 'profile-wishlist' }]"
              >
                {{ t('profile.breadcrumbs.wishlist') }}
              </router-link>
              <router-link
                  :to="translations.i18nRoute({ name: 'profile-balance' })"
                  class="profile__nav-item"
                  :class="[{ active: routeName === 'profile-balance' }]"
              >
                {{ t('profile.breadcrumbs.balance') }}
              </router-link>
              <router-link
                  :to="translations.i18nRoute({ name: 'profile-orders' })"
                  class="profile__nav-item"
                  :class="[{ active: routeName === 'profile-orders' }]"
              >
                {{ t('profile.breadcrumbs.orders') }}
              </router-link>
            </nav>
            <ui-button @click="logout">{{ t('buttons.logOut') }}</ui-button>
          </div>
          <div class="profile__inner">
            <RouterView v-slot="{ Component }">
              <Transition name="opacity" mode="out-in">
                <component :is="Component" />
              </Transition>
            </RouterView>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {useRoute, useRouter} from "vue-router";
import {computed, onMounted} from "vue";
import translations from "@/core/helpers/translations.js";
import {useI18n} from "vue-i18n";
import UiButton from "@/components/ui/ui-button.vue";
import {useAuthStore} from "@/stores/auth.js";

const {t} = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const routeName = computed(() => route.name)

const getBreadcrumb = () => {
  switch (routeName.value) {
    case "profile-info":
      return t('profile.breadcrumbs.info')

    case "profile-cart":
      return t('profile.breadcrumbs.cart')

    case "profile-wishlist":
      return t('profile.breadcrumbs.wishlist')

    case "profile-balance":
      return t('profile.breadcrumbs.balance')

    case "profile-orders":
      return t('profile.breadcrumbs.orders')

    default:
      return t('profile.breadcrumbs.info')
  }
}

const logout = async () => {
  authStore.setUser({
    user: null,
    accessToken: null
  })

  localStorage.removeItem('awe-refresh')
  localStorage.removeItem('awe-remember')
  localStorage.removeItem('awe-login')

  await router.push({
    name: 'home',
    params: {
      locale: localStorage.getItem('awe-user-locale') || 'en-gb'
    }
  })
}

onMounted( async () => {
  if (route.name === 'profile') {
    await router.push({
      name: 'profile-info',
      params: {
        locale: localStorage.getItem('awe-user-locale') || 'en-gb'
      }
    })
  }

  // await fetchOrders()
})
</script>

<style lang="scss" scoped>
.profile {
  padding-block: 80px 100px;

  &__top {
    background-image: url(@images/profileTopBg.png);
    background-repeat: no-repeat;
    background-size: cover;
    padding-block: 45px 100px;
  }

  &__title {
    margin-top: 25px;
    color: #F3F3F3;
    font-size: 64px;
    font-weight: 700;
  }

  &__main {
    &-wrapper {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 150px;
      padding-top: 60px;
    }
  }

  &__left {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 25px;
  }

  &__nav {
    border: 2px solid #ff0000;
    box-shadow: 0 0 10px 1px #ff0000;
    padding: 20px 50px 20px 30px;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex-shrink: 0;

    &-item {
      position: relative;
      flex-shrink: 0;
      width: fit-content;
      color: #F3F3F3;
      font-size: 20px;

      &::after {
        content: "";
        position: absolute;
        bottom: -2px;
        left: 0;
        height: 2px;
        width: 0;
        transition: all .2s ease;
        background-color: #ff0000;
      }

      @include hover {
        &::after {
          width: 100%;
        }
      }

      &.active {
        &::after {
          width: 100%;
        }
      }
    }
  }

  &__inner {
    width: 100%;
  }
}

@media (max-width: 1300px) {
  .profile {
    &__main {
      &-wrapper {
        gap: 50px;
      }
    }
  }
}

@media (max-width: 1000px) {
  .profile {
    &__main {
      &-wrapper {
        flex-direction: column;
        align-items: center;
      }
    }
  }
}

@media (max-width: 750px) {
  .profile {
    &__top {
      padding-block: 30px;
    }

    &__title {
      font-size: 42px;
    }
  }
}
</style>